import { useState, useEffect, useContext, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { UserContext } from 'contexts/user';
import { format, parseISO, isValid, compareDesc } from 'date-fns';
import { isEmpty } from 'lodash';

import ViewComponent from './view_component';

import {
  getCustomerById,
  getCustomerHistory,
  updateCustomer,
} from 'api/customers';
import { createInvoice } from 'api/invoices';

const InvoiceAdd = () => {
  const { user } = useContext(UserContext);
  const [customer, setCustomer] = useState({});
  const [localCustomer, setLocalCustomer] = useState({});
  const [hidden, setHidden] = useState(true);
  const [loading, setLoading] = useState(false);
  const [act, setAct] = useState(null);
  const [allActs, setAllActs] = useState([]);
  const [error, setError] = useState();
  const history = useHistory();
  const location = useLocation();

  const [validation, setValidation] = useState({
    customer: true,
    firstName: true,
    lastName: true,
    dateOfBirth: true,
    carteVitale: true,
    act: true,
  });

  const [validationText, setValidationText] = useState({
    carteVitale: 'This question is required!',
  });

  const onCustomerSelectChange = useCallback(
    async (customer, actInfo = {}) => {
      let acts = [];

      const {
        id,
        firstName,
        lastName,
        dateOfBirth,
        carteVitale,
        center,
        fund,
        regime,
      } = await getCustomerById(customer.id);

      const { polygraphies, polysomnographies, teleconsultations } =
        await getCustomerHistory(customer.id);

      polygraphies?.forEach((pg) => {
        const deliveryDateText = pg.deliveryDate
          ? `on ${format(parseISO(pg.deliveryDate), 'dd.MM.yyyy')}`
          : '';

        if (pg.doctor?.id === user.id) {
          acts.push({
            id: `pg-${pg.id}`,
            entityId: pg.id,
            type: 'polygraphy',
            date: pg.deliveryDate,
            value: `polygraphy-${deliveryDateText}`,
            label: `Polygraphy ${deliveryDateText}`,
          });
        }
      });

      polysomnographies?.forEach((psg) => {
        const deliveryDateText = psg.deliveryDate
          ? `on ${format(parseISO(psg.deliveryDate), 'dd.MM.yyyy')}`
          : '';

        if (psg.doctor?.id === user.id) {
          acts.push({
            id: `psg-${psg.id}`,
            entityId: psg.id,
            type: 'polysomnography',
            date: psg.deliveryDate,
            value: `polysomnography-${deliveryDateText}`,
            label: `Polysomnography ${deliveryDateText}`,
          });
        }
      });

      teleconsultations?.forEach((tc) => {
        if (tc.doctor?.id === user.id) {
          acts.push({
            id: `tc-${tc.id}`,
            entityId: tc.id,
            type: 'teleconsultation',
            date: tc.startTime,
            value: `${tc.stage}-teleconsultation-on-${format(
              parseISO(tc.startTime),
              'dd.MM.yyyy',
            )}`,
            label: `${
              tc.stage === 'firstRenewal'
                ? 'First renewal'
                : tc.stage === 'annualRenewal'
                ? 'Annual renewal'
                : tc.stage[0].toUpperCase() + tc.stage.slice(1)
            } teleconsultation on ${format(
              parseISO(tc.startTime),
              'dd.MM.yyyy',
            )}`,
          });
        }
      });

      if (acts.length > 0) {
        const defaultAct = acts.find((act) => {
          return (
            act.type === actInfo.type &&
            act.entityId === Number(actInfo.entityId)
          );
        });
        setAct(defaultAct);
      }
      const customerData = {
        id,
        firstName,
        lastName,
        value: `${firstName} ${lastName}`,
        label: `${firstName} ${lastName}`,
        dateOfBirth,
        carteVitale,
        center,
        fund,
        regime,
      };
      setAllActs(acts);
      setCustomer(customerData);
      setLocalCustomer(customerData);
      setHidden(false);
    },
    [user.id],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let actInfo = {};

    for (let [key, value] of params.entries()) {
      actInfo[key] = value;
    }

    if (actInfo.customerId) {
      const customer = { id: actInfo.customerId };
      onCustomerSelectChange(customer, actInfo);
    }
  }, [onCustomerSelectChange, location.search]);

  useEffect(() => {
    if (allActs.length > 0) {
      allActs.sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : null;
        const dateB = b.date ? new Date(b.date) : null;

        switch (true) {
          case dateA === null && dateB === null:
            return 0;
          case dateA === null:
            return -1;
          case dateB === null:
            return 1;
          case isValid(dateA) && isValid(dateB):
            return compareDesc(dateA, dateB);
          default:
            return 0;
        }
      });
      if (!act) {
        setAct(allActs[0]);
      }
    }
  }, [allActs, act]);

  const checkValidity = useCallback(() => {
    const isCartVitaleValid = /^\d{6}[0-9AB]\d{8}$/.test(
      localCustomer.carteVitale,
    );

    const validation = {
      customer: !isEmpty(localCustomer),
      firstName: !!localCustomer.firstName?.trim(),
      lastName: !!localCustomer.lastName?.trim(),
      dateOfBirth: !!localCustomer?.dateOfBirth,
      carteVitale: isCartVitaleValid,
      act: !isEmpty(act),
    };

    const newValidationText = { ...validationText };

    if (!localCustomer.carteVitale || localCustomer.carteVitale?.length === 0) {
      newValidationText.carteVitale = 'This question is required!';
    } else if (localCustomer.carteVitale?.length !== 15) {
      newValidationText.carteVitale =
        'Carte Vitale number must contain 15 digits';
    } else if (!isCartVitaleValid && !!localCustomer.carteVitale) {
      newValidationText.carteVitale = 'Carte Vitale is invalid';
    }

    setValidation(validation);
    setValidationText(newValidationText);
    return Object.values(validation).every(Boolean);
  }, [localCustomer, act, validationText]);

  const onCustomerChange = (e) => {
    const { name, value } = e.target;
    const trimmedValue =
      name === 'firstName' || name === 'lastName' ? value : value.trim();

    if (name === 'carteVitale') {
      return setLocalCustomer({
        ...localCustomer,
        [name]: value.toUpperCase(),
      });
    }

    setLocalCustomer({ ...localCustomer, [name]: trimmedValue });
  };

  const onActChange = async (act) => {
    setAct(act);
  };

  const onDateChange = ({ value }) => {
    value.setHours(4, 0, 0, 0); // set to 4am to avoid timezone issues

    setLocalCustomer({ ...localCustomer, dateOfBirth: value });
  };

  const onSubmit = useCallback(async () => {
    setLoading(true);

    if (checkValidity()) {
      if (JSON.stringify(localCustomer) !== JSON.stringify(customer)) {
        const { id, firstName, lastName, carteVitale, dateOfBirth } =
          localCustomer;

        await updateCustomer(id, {
          firstName,
          lastName,
          carteVitale,
          dateOfBirth,
        });
      }
      const res = await createInvoice({
        doctorId: user.id,
        customer: localCustomer,
        act,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setError();
        history.goBack();

        window.open(
          `${process.env.REACT_APP_INTELLIO_URL}${res.detail}`,
          '_blank',
        );
      }
    }

    setLoading(false);
  }, [checkValidity, customer, act]);

  return (
    <ViewComponent
      onCustomerChange={onCustomerChange}
      onCustomerSelectChange={onCustomerSelectChange}
      onActChange={onActChange}
      onSubmit={onSubmit}
      onDateChange={onDateChange}
      customer={localCustomer}
      act={act}
      hidden={hidden}
      loading={loading}
      error={error}
      actSelectOptions={allActs}
      validation={validation}
      validationText={validationText}
    />
  );
};
export default InvoiceAdd;
